import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Sparkles, Clock, MessageCircle, RotateCcw } from 'lucide-react';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import VoiceInput from '@/components/VoiceInput';
import { trackExerciseSubmitted } from '@/lib/analytics';

const ExerciseDetail = () => {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const { language } = useContext(AuthContext);
  const [exercise, setExercise] = useState(null);
  const [response, setResponse] = useState('');
  const [existingResponse, setExistingResponse] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const t = (es, ca) => language === 'ca' ? ca : es;

  useEffect(() => {
    fetchExercise();
    fetchExistingResponse();
  }, [exerciseId]);

  const fetchExercise = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/exercises/${exerciseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExercise(res.data);
    } catch (error) {
      console.error('Error fetching exercise:', error);
      toast.error(t('Error al cargar ejercicio', 'Error en carregar exercici'));
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingResponse = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/exercises/${exerciseId}/response`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExistingResponse(res.data);
    } catch (error) {
      // No previous response, that's okay
      setExistingResponse(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (response.trim().length < 50) {
      toast.error(t('La respuesta debe tener al menos 50 caracteres', 'La resposta ha de tenir almenys 50 caràcters'));
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/exercises/submit`,
        { 
          exercise_id: exerciseId,
          response_text: response.trim(), 
          language 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setExistingResponse(res.data);
      trackExerciseSubmitted(exerciseId, exercise?.title_ca || exercise?.title_es);
      setResponse('');
      toast.success(t('¡Reflexión enviada!', 'Reflexió enviada!'));
    } catch (error) {
      console.error('Error submitting response:', error);
      toast.error(t('Error al enviar', 'Error en enviar'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950 flex items-center justify-center">
        <div className="text-teal-300 text-lg">{t('Cargando...', 'Carregant...')}</div>
      </div>
    );
  }

  if (!exercise) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950 flex items-center justify-center">
        <div className="text-red-400">{t('Ejercicio no encontrado', 'Exercici no trobat')}</div>
      </div>
    );
  }

  const title = language === 'ca' ? exercise.title_ca : exercise.title_es;
  const prompt = language === 'ca' ? exercise.prompt_ca : exercise.prompt_es;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <button
          onClick={() => navigate('/exercises')}
          className="flex items-center gap-2 text-gray-400 hover:text-teal-300 transition-colors mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{t('Volver a Ejercicios', 'Tornar a Exercicis')}</span>
        </button>

        <div className="glass rounded-2xl p-8 fade-in">
          {/* Title */}
          <div className="flex items-center gap-3 mb-6">
            <Sparkles className="w-8 h-8 text-purple-400" />
            <h1 className="text-3xl font-bold text-purple-300">{title}</h1>
          </div>

          {/* Duration */}
          <div className="flex items-center gap-2 text-gray-400 mb-6">
            <Clock className="w-4 h-4" />
            <span className="text-sm">{exercise.duration_minutes} {t('minutos', 'minuts')}</span>
          </div>

          {/* Prompt */}
          <div className="prose prose-invert max-w-none mb-8">
            <p className="text-gray-200 leading-relaxed whitespace-pre-wrap">{prompt}</p>
          </div>

          {/* Response Form or Existing Response */}
          {existingResponse ? (
            <div className="space-y-6">
              {/* User Response */}
              <div className="card glass rounded-2xl p-6 border-teal-400/30">
                <h3 className="text-xl font-semibold mb-4 text-teal-300">
                  {t('Tu Reflexión', 'La Teva Reflexió')}
                </h3>
                <p className="text-gray-200 leading-relaxed whitespace-pre-wrap">
                  {existingResponse.response_text || existingResponse.response}
                </p>
                <p className="text-xs text-gray-500 mt-4">
                  {new Date(existingResponse.completed_at).toLocaleDateString(language === 'ca' ? 'ca-ES' : 'es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>

              {/* AI Feedback */}
              {existingResponse.ai_feedback && (
                <div className="card glass rounded-2xl p-6 border-purple-400/30">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-purple-400" />
                    <h3 className="text-xl font-semibold text-purple-300">
                      {t('Guía de la IA', 'Guia de la IA')}
                    </h3>
                  </div>
                  <p className="text-gray-200 leading-relaxed whitespace-pre-wrap">
                    {existingResponse.ai_feedback}
                  </p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {/* Botó per xatejar sobre l'exercici */}
                <button
                  onClick={() => navigate(`/chat?context=exercise&id=${exerciseId}&title=${encodeURIComponent(title)}`)}
                  className="btn-secondary px-6 py-3 flex items-center gap-2"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>{t('Conversar con la IA', 'Conversar amb la IA')}</span>
                </button>

                {/* Botó per fer l'exercici de nou */}
              <button
                onClick={() => setExistingResponse(null)}
                className="px-6 py-3 flex items-center gap-2 text-gray-400 hover:text-teal-300 transition-colors"
              >
                <RotateCcw className="w-5 h-5" />
                <span>{t('Hacer de nuevo', 'Fer l’exercici de nou')}</span>
              </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('Tu Reflexión', 'La Teva Reflexió')}
                  <span className="text-gray-500 ml-2">({t('mínimo 50 caracteres', 'mínim 50 caràcters')})</span>
                </label>
                <div className="relative">
                  <textarea
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    placeholder={t(
                      'Escribe tu reflexión profunda sobre este ejercicio...',
                      'Escriu la teva reflexió profunda sobre aquest exercici...'
                    )}
                    className="w-full px-4 py-3 pr-14 bg-black/30 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all resize-none"
                    rows={8}
                  />
                  <div className="absolute bottom-3 right-3">
                    <VoiceInput 
                      onTranscription={(text) => setResponse(response + (response ? ' ' : '') + text)}
                      disabled={submitting}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {response.length} {t('caracteres', 'caràcters')}
                </p>
              </div>

              <button
                type="submit"
                disabled={submitting || response.trim().length < 50}
                className="btn-primary flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{t('Enviando...', 'Enviant...')}</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>{t('Enviar Respuesta y Recibir Reflexión', 'Enviar Resposta i Rebre Reflexió')}</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExerciseDetail;
